import type {
    WebformFontFamily,
    WebformFontSize,
    WebformFontRole,
    WebformFontRoleKey,
    WebformFonts,
    WebformFontWeight,
    WebformHeader,
    WebformHeaderLevel,
    WebformMaxWidth,
} from './types'

// Font + header resolution shared by the embed bundle and the admin preview,
// so both agree on exactly what a config renders as. Everything here is
// defensive: the embed runs inside a sandboxed iframe on clinics' own sites,
// so we re-validate what the API gives us rather than trusting it.

export const DEFAULT_FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

type FontSource = { url: string; unicodeRange: string }

type CuratedFont = {
    stack: string
    // Present for webfonts we host a file for; absent for stacks that rely on
    // fonts the visitor's device already has.
    face?: {
        name: string
        // Variable fonts, so one file covers the whole 300–700 weight range and
        // a header line set to 500 renders at 500 rather than being synthesised.
        weightRange: string
        sources: FontSource[]
    }
}

// The two subsets a UK/EU clinic form actually needs. Splitting them (rather
// than shipping one file) means the accented-character file is only fetched
// when a page actually uses those glyphs.
const LATIN = 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
const LATIN_EXT = 'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF'

// Root-relative on purpose: the files are shipped in each bundle's public/fonts
// (app/ for the admin preview, forms/ for the embed iframe), so a form only ever
// fetches fonts from the origin already serving it — no third-party CDN, and no
// cross-origin font request to need CORS.
const selfHosted = (name: string, slug: string): CuratedFont['face'] => ({
    name,
    // One variable file per subset covers the whole 300-700 range.
    weightRange: '300 700',
    sources: [
        { url: `/fonts/${slug}-latin-ext.woff2`, unicodeRange: LATIN_EXT },
        { url: `/fonts/${slug}-latin.woff2`, unicodeRange: LATIN },
    ],
})

export const CURATED_FONTS: Record<Exclude<WebformFontFamily, 'custom'>, CuratedFont> = {
    system: { stack: DEFAULT_FONT_STACK },
    helvetica: { stack: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
    georgia: { stack: 'Georgia, "Times New Roman", Times, serif' },
    times: { stack: '"Times New Roman", Times, serif' },
    courier: { stack: '"Courier New", Courier, monospace' },
    inter: {
        stack: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif',
        face: selfHosted('Inter', 'inter'),
    },
    'cormorant-garamond': {
        stack: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
        face: selfHosted('Cormorant Garamond', 'cormorant-garamond'),
    },
    montserrat: {
        stack: 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif',
        face: selfHosted('Montserrat', 'montserrat'),
    },
    mulish: {
        stack: 'Mulish, "Helvetica Neue", Helvetica, Arial, sans-serif',
        face: selfHosted('Mulish', 'mulish'),
    },
}

// A brand font is a name, not a CSS stack — clinics give us "Gilroy", we add
// the fallbacks. Keeping it to one token means it can be interpolated into the
// @font-face rule below without opening a CSS-injection hole.
export const CUSTOM_FONT_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,63}$/

const FONT_FILE_FORMATS: Record<string, string> = {
    '.woff2': 'woff2',
    '.woff': 'woff',
    '.ttf': 'truetype',
    '.otf': 'opentype',
}

export const FONT_ROLE_KEYS: WebformFontRoleKey[] = ['heading', 'label', 'body']

export function sanitiseCustomFontName(value: string | null | undefined): string | null {
    const trimmed = value?.trim()
    if (!trimmed || !CUSTOM_FONT_NAME_RE.test(trimmed)) return null
    return trimmed
}

// Only https URLs pointing directly at a font file are accepted. We deliberately
// do not support stylesheet URLs (e.g. Google Fonts CSS): a third-party
// stylesheet injected into the form could exfiltrate typed values via attribute
// selectors, whereas a font file is inert.
export function safeFontUrl(value: string | null | undefined): { url: string; format: string } | null {
    const trimmed = value?.trim()
    if (!trimmed) return null
    let parsed: URL
    try {
        parsed = new URL(trimmed)
    } catch {
        return null
    }
    if (parsed.protocol !== 'https:') return null
    const extension = Object.keys(FONT_FILE_FORMATS).find(ext => parsed.pathname.toLowerCase().endsWith(ext))
    if (!extension) return null
    // url() is single-quoted below, so a quote or backslash in the URL would
    // break out of it. Percent-encode rather than reject — both are legal in a
    // URL, just not in our rule.
    const url = parsed.toString().replace(/\\/g, '%5C').replace(/'/g, '%27')
    return { url, format: FONT_FILE_FORMATS[extension] }
}

type ResolvedFace = { name: string; weightRange?: string; sources: (FontSource & { format: string })[] }

function resolveRole(role: WebformFontRole | undefined): { stack: string; face: ResolvedFace | null } {
    if (!role) return { stack: DEFAULT_FONT_STACK, face: null }

    if (role.family !== 'custom') {
        const curated = CURATED_FONTS[role.family]
        if (!curated) return { stack: DEFAULT_FONT_STACK, face: null }
        const face = curated.face
            ? {
                name: curated.face.name,
                weightRange: curated.face.weightRange,
                sources: curated.face.sources.map(s => ({ ...s, format: 'woff2' })),
            }
            : null
        return { stack: curated.stack, face }
    }

    const name = sanitiseCustomFontName(role.customFamily)
    if (!name) return { stack: DEFAULT_FONT_STACK, face: null }
    const source = safeFontUrl(role.customUrl)
    return {
        stack: `"${name}", ${DEFAULT_FONT_STACK}`,
        // Without a hosted file the name still works for visitors who have the
        // font installed; there is just nothing to load.
        face: source ? { name, sources: [{ url: source.url, format: source.format, unicodeRange: '' }] } : null,
    }
}

export type ResolvedFonts = {
    heading: string
    label: string
    body: string
    // All @font-face rules the form needs, deduped; empty string when none.
    fontFaceCss: string
}

// `headers` is passed so per-line font overrides get their @font-face emitted
// too — a line set to Montserrat must load Montserrat even when no role uses it.
export function resolveFonts(
    fonts: WebformFonts | undefined,
    headers: WebformHeader[] = [],
): ResolvedFonts {
    const roleFaces = FONT_ROLE_KEYS.map(key => [key, resolveRole(fonts?.[key])] as const)
    const perLineFaces = headers
        .map(h => headerFontFamily(h))
        .filter((f): f is Exclude<WebformFontFamily, 'custom'> => f !== null)
        .map(family => resolveRole({ family }))
    const resolved = [...roleFaces.map(([, r]) => r), ...perLineFaces]

    const rules = new Map<string, string>()
    for (const { face } of resolved) {
        if (!face) continue
        for (const source of face.sources) {
            const rule = [
                `font-family:"${face.name}"`,
                'font-style:normal',
                face.weightRange ? `font-weight:${face.weightRange}` : null,
                `src:url('${source.url}') format('${source.format}')`,
                'font-display:swap',
                source.unicodeRange ? `unicode-range:${source.unicodeRange}` : null,
            ].filter(Boolean).join(';')
            // Two roles can share a font — emit each rule once.
            rules.set(`${face.name}|${source.url}`, `@font-face{${rule};}`)
        }
    }
    const stacks = Object.fromEntries(roleFaces.map(([key, { stack }]) => [key, stack]))
    return {
        heading: stacks.heading,
        label: stacks.label,
        body: stacks.body,
        fontFaceCss: [...rules.values()].join(''),
    }
}

// The widths a form may be capped to. null = fill whatever container the clinic
// pasted the snippet into, which is how every form rendered before this existed.
export const MAX_WIDTHS: WebformMaxWidth[] = [320, 360, 400, 480, 560, 640, 720, 800, 960]

export function resolveMaxWidth(maxWidth: WebformMaxWidth | null | undefined): number | null {
    if (!maxWidth || !MAX_WIDTHS.includes(maxWidth)) return null
    return maxWidth
}

// The weights the form has always used. A header line with no explicit weight
// keeps these, so an untouched form renders exactly as it did before weights
// were configurable.
export const DEFAULT_HEADER_WEIGHTS: Record<WebformHeaderLevel, WebformFontWeight> = {
    h1: 700,
    h2: 600,
    h3: 600,
    body: 400,
}

export const HEADER_WEIGHTS: WebformFontWeight[] = [300, 400, 500, 600, 700]

// The sizes the standard levels have always used, so an untouched line is
// unchanged; the rest give room to go bigger or smaller.
export const DEFAULT_HEADER_SIZES: Record<WebformHeaderLevel, WebformFontSize> = {
    h1: 26 as WebformFontSize,
    h2: 20,
    h3: 16,
    body: 14,
}

export const HEADER_SIZES: WebformFontSize[] = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48]

export function resolveHeaderSize(header: Pick<WebformHeader, 'level' | 'size'>): number {
    const size = header.size
    if (size && HEADER_SIZES.includes(size)) return size
    return DEFAULT_HEADER_SIZES[header.level] ?? 14
}

// Families a single header line may pick. Brand fonts need a name and a file,
// which are configured once per role — so they are not offered per line.
export const HEADER_FONT_FAMILIES = Object.keys(CURATED_FONTS) as Exclude<WebformFontFamily, 'custom'>[]

function headerFontFamily(
    header: Pick<WebformHeader, 'font'>,
): Exclude<WebformFontFamily, 'custom'> | null {
    const font = header.font
    if (!font || !HEADER_FONT_FAMILIES.includes(font)) return null
    return font
}

// A line's own font wins; otherwise it inherits the role for its level.
export function resolveHeaderStack(
    header: Pick<WebformHeader, 'level' | 'font'>,
    fonts: ResolvedFonts,
): string {
    const own = headerFontFamily(header)
    if (own) return CURATED_FONTS[own].stack
    return header.level === 'body' ? fonts.body : fonts.heading
}

export function resolveHeaderWeight(header: Pick<WebformHeader, 'level' | 'weight'>): WebformFontWeight {
    const weight = header.weight
    if (weight && HEADER_WEIGHTS.includes(weight)) return weight
    return DEFAULT_HEADER_WEIGHTS[header.level] ?? 400
}

const VALID_LEVELS: WebformHeaderLevel[] = ['h1', 'h2', 'h3', 'body']

// Configs created before headers existed (and any config whose headers were
// cleared) still render their title/subtitle, unchanged — title as a heading,
// subtitle as body copy, both left-aligned.
export function resolveHeaders(
    headers: WebformHeader[] | undefined,
    title: string,
    subtitle?: string | null,
): WebformHeader[] {
    const explicit = (headers ?? []).filter(h => h.text?.trim() && VALID_LEVELS.includes(h.level))
    if (explicit.length > 0) return explicit

    const fallback: WebformHeader[] = [
        { id: 'title', text: title || 'Untitled form', level: 'h2', align: 'left' },
    ]
    if (subtitle?.trim()) {
        fallback.push({ id: 'subtitle', text: subtitle, level: 'body', align: 'left' })
    }
    return fallback
}

// ---------------------------------------------------------------------------
// Surfaces
//
// A form panel can now be any colour the clinic picks, including black. Rather
// than add a "dark mode" switch for them to get wrong, every colour that sits
// on a surface is derived from that surface's luminance — so a dark panel gets
// light ink, hairline borders and see-through inputs, and a light one renders
// exactly the greys it always has.

const SHORT_HEX_RE = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i
const HEX_RE = /^#([0-9a-f]{6})$/i

export function parseHex(value: string | null | undefined): { r: number; g: number; b: number } | null {
    const trimmed = value?.trim()
    if (!trimmed) return null
    const expanded = trimmed.replace(SHORT_HEX_RE, (_, r, g, b) => `#${r}${r}${g}${g}${b}${b}`)
    const match = HEX_RE.exec(expanded)
    if (!match) return null
    const int = parseInt(match[1], 16)
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 }
}

// WCAG relative luminance. Used only to choose between a light and a dark ink,
// so the exact value matters less than the ordering being correct.
export function relativeLuminance(value: string | null | undefined): number {
    const rgb = parseHex(value)
    // An unparseable colour is treated as light: the original palette, which is
    // what a config with a malformed hex used to render as.
    if (!rgb) return 1
    const channel = (c: number) => {
        const s = c / 255
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    }
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

// The ink to print *on* a colour — used for button labels, where the background
// is chosen by the clinic and the label has to stay readable either way.
export const DARK_INK = '#111111'
export const LIGHT_INK = '#FFFFFF'

// Whichever of the two inks contrasts better, rather than a guessed luminance
// cut-off. The boundary this lands on (~0.179) is where white and black contrast
// equally, so a mid-tone like a taupe button correctly gets dark text even
// though it reads as "quite dark" by eye.
export function inkOn(value: string | null | undefined): string {
    return contrastRatio(LIGHT_INK, value) > contrastRatio(DARK_INK, value) ? LIGHT_INK : DARK_INK
}

// A surface is "dark" exactly when light ink is the readable choice on it — so
// the panel palette and the button labels can never disagree about a colour.
export function isDarkSurface(value: string | null | undefined): boolean {
    return inkOn(value) === LIGHT_INK
}

export type SurfacePalette = {
    dark: boolean
    // Headings and field labels.
    strong: string
    // Body copy and secondary lines.
    muted: string
    // Input borders and hairline rules.
    border: string
    inputBackground: string
    inputInk: string
    inputPlaceholder: string
}

// The light values are the literals the renderer has always used, so a form on
// a light panel is byte-for-byte unchanged. The dark values are translucent
// white rather than fixed greys, so they sit correctly on any dark hue.
const LIGHT_SURFACE: Omit<SurfacePalette, 'dark'> = {
    strong: '#222',
    muted: '#555',
    border: '#d6dadf',
    inputBackground: '#fff',
    inputInk: '#222',
    // 5:1 on white. Placeholder text carries the field's only visible name
    // when labelStyle is 'placeholder', so it has to clear AA on its own.
    inputPlaceholder: '#6b7079',
}

const DARK_SURFACE: Omit<SurfacePalette, 'dark'> = {
    strong: '#FFFFFF',
    muted: 'rgba(255,255,255,0.72)',
    border: 'rgba(255,255,255,0.45)',
    // Transparent, so the panel colour shows through the box exactly as an
    // outlined field should.
    inputBackground: 'transparent',
    inputInk: '#FFFFFF',
    inputPlaceholder: 'rgba(255,255,255,0.62)',
}

export function surfacePalette(background: string | null | undefined): SurfacePalette {
    const dark = isDarkSurface(background)
    return { dark, ...(dark ? DARK_SURFACE : LIGHT_SURFACE) }
}

// A heading colour the clinic picked for a light page can be invisible once the
// same text moves onto a dark panel. Fall back to the panel's own strong ink
// when the two are too close to read.
// WCAG AA for normal text. Header lines run from 12px up, so the smallest are
// held to the normal-text bar rather than the 3:1 large-text one.
const MIN_CONTRAST_RATIO = 4.5

export function contrastRatio(a: string | null | undefined, b: string | null | undefined): number {
    const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x)
    return (light + 0.05) / (dark + 0.05)
}

export function readableOn(colour: string, background: string | null | undefined, palette: SurfacePalette): string {
    return contrastRatio(colour, background) >= MIN_CONTRAST_RATIO ? colour : palette.strong
}
